import React, { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { LoginSchema, type LoginInput } from '../schemas/auth';
import { Button, Field, FormGrid } from './ui';
import { AuthAlert, AuthLayout } from './auth/AuthLayout';

// Issue #688 pilot: this form is the first migration to
// react-hook-form + Zod. Validation rules live in
// `frontend/src/schemas/auth.ts` (shared with future screens).
const Login: React.FC = () => {
  const [submitError, setSubmitError] = useState('');
  const login = useAuthStore((s) => s.login);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    mode: 'onSubmit',
    defaultValues: { email: '', password: '' },
  });

  const onSubmit: SubmitHandler<LoginInput> = async (values) => {
    setSubmitError('');
    try {
      await login(values.email, values.password);
    } catch {
      setSubmitError('ログインに失敗しました');
    }
  };

  return (
    <AuthLayout
      title="ログイン"
      links={
        <Link to="/forgot-password" className="ui-auth__link">
          パスワードをお忘れですか？
        </Link>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {submitError && <AuthAlert>{submitError}</AuthAlert>}
        <FormGrid>
          <Field label="メールアドレス" id="login-email" error={errors.email?.message}>
            <input type="email" autoComplete="email" {...register('email')} />
          </Field>
          <Field label="パスワード" id="login-password" error={errors.password?.message}>
            <input type="password" autoComplete="current-password" {...register('password')} />
          </Field>
        </FormGrid>
        <Button
          type="submit"
          variant="primary"
          block
          loading={isSubmitting}
          iconLeft={<LogIn aria-hidden="true" />}
          className="ui-auth__submit"
        >
          {isSubmitting ? 'ログイン中…' : 'ログイン'}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
